import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import type { NavigateFunction } from 'react-router-dom';
import { toast } from 'sonner';

import { ApiError } from './api';

export type FieldErrors = Record<string, string[]>;

type ApiProblemInit = {
  type: string;
  status: number;
  title: string;
  detail?: string;
  instance?: string;
  errors?: FieldErrors;
  requestId?: string;
};

type ProblemBody = Partial<ApiProblemInit> & { request_id?: unknown };

function isProblemBody(value: unknown): value is ProblemBody {
  return typeof value === 'object' && value !== null && 'type' in value && 'title' in value;
}

export class ApiProblem extends Error {
  readonly type: string;
  readonly status: number;
  readonly title: string;
  readonly detail?: string;
  readonly instance?: string;
  readonly errors?: FieldErrors;
  readonly requestId?: string;

  constructor(init: ApiProblemInit) {
    super(init.title);
    this.type = init.type;
    this.status = init.status;
    this.title = init.title;
    this.detail = init.detail;
    this.instance = init.instance;
    this.errors = init.errors;
    this.requestId = init.requestId;
  }

  static fromApiError(e: ApiError): ApiProblem {
    if (isProblemBody(e.body)) {
      const body = e.body;
      return new ApiProblem({
        type: String(body.type ?? 'unknown'),
        status: e.status,
        title: String(body.title ?? `Request failed (${e.status})`),
        detail: body.detail ? String(body.detail) : undefined,
        instance: body.instance ? String(body.instance) : undefined,
        errors: body.errors as FieldErrors | undefined,
        requestId: body.request_id ? String(body.request_id) : undefined,
      });
    }
    return new ApiProblem({
      type: 'unknown',
      status: e.status,
      title: e.status >= 500 ? 'Server error' : 'Request failed',
      detail: typeof e.body === 'string' ? e.body : undefined,
    });
  }

  static network(message = 'You appear to be offline'): ApiProblem {
    return new ApiProblem({ type: 'network', status: 0, title: message });
  }

  isValidation(): this is ApiProblem & { errors: FieldErrors } {
    return !!this.errors && Object.keys(this.errors).length > 0;
  }
}

export function toApiProblem(error: unknown): ApiProblem {
  if (error instanceof ApiProblem) return error;
  if (error instanceof ApiError) return ApiProblem.fromApiError(error);
  if (error instanceof TypeError) return ApiProblem.network();
  if (error instanceof Error) {
    return new ApiProblem({ type: 'unknown', status: 0, title: error.message });
  }
  return new ApiProblem({ type: 'unknown', status: 0, title: 'Unknown error' });
}

// Laravel's own validation messages are full sentences that already name the field, and
// the backend humanizes known InPost payload fields into sentences ("Invalid sender
// postal code"). Only bare single-token codes ("invalid", "required") that slip through
// for unmapped fields still need their field path for context.
function flattenFieldErrors(problem: ApiProblem & { errors: FieldErrors }): string[] {
  return Object.entries(problem.errors).flatMap(([field, messages]) => {
    const list = Array.isArray(messages) ? messages : [messages];
    if (problem.type === 'validation_failed') return list;
    return list.map((message) => {
      const text = String(message);
      return text.includes(' ') ? text : `${field}: ${text}`;
    });
  });
}

export function toastProblem(problem: ApiProblem): void {
  const fieldMessages = problem.isValidation() ? flattenFieldErrors(problem) : [];
  const paragraphs = (fieldMessages.length ? fieldMessages : [problem.detail]).filter(
    (line): line is string => Boolean(line),
  );
  const ref = problem.requestId ? `Ref: ${problem.requestId.slice(0, 8)}` : null;

  if (!paragraphs.length && !ref) {
    toast.error(problem.title);
    return;
  }

  toast.error(problem.title, {
    description: (
      <div className="space-y-1.5">
        {paragraphs.map((line, index) => (
          <p key={index}>{line}</p>
        ))}
        {ref && <p className="opacity-70">{ref}</p>}
      </div>
    ),
  });
}

export function applyApiErrors<T extends FieldValues>(
  form: UseFormReturn<T>,
  problem: ApiProblem,
): void {
  if (!problem.isValidation()) {
    toastProblem(problem);
    return;
  }
  for (const [path, messages] of Object.entries(problem.errors)) {
    const message = messages[0];
    if (!message) continue;
    form.setError(path as Path<T>, { type: 'server', message });
  }
  toast.error(problem.title);
}

export type ProblemDispatch = (navigate: NavigateFunction, problem: ApiProblem) => void;

export const dispatchProblem: ProblemDispatch = (navigate, problem) => {
  switch (problem.type) {
    case 'inpost_connection_missing_token':
    case 'no_connection':
      toastProblem(problem);
      navigate('/carriers');
      return;
    case 'account_not_accessible':
      toastProblem(problem);
      navigate('/shipments');
      return;
    default:
      toastProblem(problem);
  }
};
